import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { type Dayjs } from 'dayjs';
import React, { useRef, useState } from 'react';
import Tooltip from '@mui/material/Tooltip';
import Popover from '@mui/material/Popover';
import type { UpdateTimeEntryDto } from 'api';
import type { DetailedEntry } from '../utils/reportsUtils';
import { toHHMM } from '../../timer/utils/trackerUtils';
import {
  DetailedTimeEditContainer,
  DetailedTimeInput,
  DetailedTimeSeparator,
  DetailedTimeRange,
  DetailedTimeCellContainer,
  DetailedDateButton,
} from '../reports.styles';

interface EditableTimeCellProps {
  entry: DetailedEntry;
  onUpdate?: (id: string, data: UpdateTimeEntryDto) => void;
}

export const EditableTimeCell: React.FC<EditableTimeCellProps> = ({ entry, onUpdate }) => {
  const [editingTime, setEditingTime] = useState(false);
  const [calendarAnchor, setCalendarAnchor] = useState<HTMLElement | null>(null);
  const [draftStart, setDraftStart] = useState(toHHMM(entry.startedAt));
  const [draftEnd, setDraftEnd] = useState(entry.endedAt ? toHHMM(entry.endedAt) : '');
  const [draftDate, setDraftDate] = useState<Dayjs>(dayjs(entry.date));
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync drafts when entry changes from server
  React.useEffect(() => {
    if (!editingTime) {
      setDraftStart(toHHMM(entry.startedAt));
      setDraftEnd(entry.endedAt ? toHHMM(entry.endedAt) : '');
      setDraftDate(dayjs(entry.date));
    }
  }, [entry.startedAt, entry.endedAt, entry.date, editingTime]);

  const buildTimeUpdate = (overrides?: {
    start?: string;
    end?: string;
    date?: Dayjs;
  }): UpdateTimeEntryDto => {
    const start = overrides?.start ?? draftStart;
    const end = overrides?.end ?? draftEnd;
    const date = overrides?.date ?? draftDate;
    const dateStr = date.format('YYYY-MM-DD');

    const startDate = new Date(`${dateStr}T${start}:00`);
    let endedAt: string | undefined;
    if (end) {
      let endDate = new Date(`${dateStr}T${end}:00`);
      if (endDate <= startDate) {
        endDate = new Date(endDate.getTime() + 24 * 60 * 60 * 1000);
      }
      endedAt = endDate.toISOString();
    }

    if (!entry.projectId) {
      throw new Error('Project ID is required for time entry update');
    }
    return {
      projectId: entry.projectId as string,
      description: entry.description || undefined,
      startedAt: startDate.toISOString(),
      endedAt,
      isBillable: entry.isBillable,
    };
  };

  const scheduleSave = (overrides?: Parameters<typeof buildTimeUpdate>[0]) => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      const start = overrides?.start ?? draftStart;
      const end = overrides?.end ?? draftEnd;
      const date = overrides?.date ?? draftDate;

      const startSame = start === toHHMM(entry.startedAt);
      const endSame = end === (entry.endedAt ? toHHMM(entry.endedAt) : '');
      const dateSame = date.format('YYYY-MM-DD') === entry.date;

      if (startSame && endSame && dateSame) return;
      onUpdate?.(entry.id, buildTimeUpdate(overrides));
    }, 300);
  };

  const handleTimeSave = () => {
    setEditingTime(false);
    scheduleSave({ start: draftStart, end: draftEnd });
  };

  const displayTimeRange = entry.endedAt
    ? `${toHHMM(entry.startedAt)} - ${toHHMM(entry.endedAt)}`
    : `${toHHMM(entry.startedAt)} -`;

  return (
    <DetailedTimeCellContainer>
      {editingTime ? (
        <DetailedTimeEditContainer
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node)) {
              handleTimeSave();
            }
          }}
        >
          <DetailedTimeInput
            autoFocus
            value={draftStart}
            onChange={(e) => setDraftStart(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleTimeSave();
              if (e.key === 'Escape') {
                setDraftStart(toHHMM(entry.startedAt));
                setDraftEnd(entry.endedAt ? toHHMM(entry.endedAt) : '');
                setEditingTime(false);
              }
            }}
            maxLength={5}
          />
          <DetailedTimeSeparator>-</DetailedTimeSeparator>
          <DetailedTimeInput
            value={draftEnd}
            onChange={(e) => setDraftEnd(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleTimeSave();
              if (e.key === 'Escape') {
                setDraftStart(toHHMM(entry.startedAt));
                setDraftEnd(entry.endedAt ? toHHMM(entry.endedAt) : '');
                setEditingTime(false);
              }
            }}
            maxLength={5}
          />
        </DetailedTimeEditContainer>
      ) : (
        <DetailedTimeRange onClick={onUpdate ? () => setEditingTime(true) : undefined}>
          {displayTimeRange}
        </DetailedTimeRange>
      )}
      <Tooltip title={onUpdate ? 'Change date' : ''}>
        <DetailedDateButton
          as="span"
          onClick={onUpdate ? (e) => setCalendarAnchor(e.currentTarget) : undefined}
        >
          {dayjs(entry.date).format('DD/MM/YYYY')}
        </DetailedDateButton>
      </Tooltip>
      <Popover
        open={Boolean(calendarAnchor)}
        anchorEl={calendarAnchor}
        onClose={() => setCalendarAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateCalendar
            value={draftDate}
            onChange={(newDate) => {
              if (newDate) {
                setDraftDate(newDate);
                setCalendarAnchor(null);
                scheduleSave({ date: newDate });
              }
            }}
          />
        </LocalizationProvider>
      </Popover>
    </DetailedTimeCellContainer>
  );
};
